import { format } from "date-fns";

import { hasMentionPills } from "components/mentions/mentions.util";
import { getHtmlLikeText } from "components/word-processor-field/word-processor-value.utils";
import {
  TASK_PRIORITY_NOT_SET,
  TASK_PRIORITY_OPTIONS
} from "constants/staff-tasks";
import type { CurrentUser } from "features/auth/auth-slice";
import type {
  StaffTask,
  StaffTaskInput
} from "services/types/staff-tasks.type";
import { convertHtmlToText, formatBackendDateOnly } from "utils/common";

import { Patient } from "../../services/types/patients.type";

export type StaffTaskV2FormValues = {
  id?: number;
  title: string;
  priority?: string;
  status?: string | number;
  description: string;
  assignee: { label: string; value: string } | null;
  assignees: { label: string; value: string }[];
  /** Teams (TEAMS_FEATURE): the team the task is sent to. With no assignee it is Unclaimed. */
  team?: { label: string; value: string; color?: string } | null;
  due_at: string | null;
  // Only `id` is read. With the full Patient, FieldPath is ~49.6k paths: one
  // `useWatch({ name: "assignee" })` took 497s and `yarn type-check` stalled (ENG-2531).
  patient: Pick<Patient, "id"> | null;
  color?: string | null;
};

export const EMPTY_DEFAULT_TASK_VALUES: StaffTaskV2FormValues = {
  title: "",
  priority: "0",
  description: "",
  status: "",
  assignees: [],
  assignee: null,
  team: null,
  due_at: "",
  patient: null,
  color: null
};

// Practice setting "Require a priority level" (staff_task_settings): when on,
// "Not Set" (value 0) is not a real priority and is removed from the options
// so it can't be picked.
export const getTaskPriorityOptions = (isPriorityRequired: boolean) =>
  isPriorityRequired
    ? TASK_PRIORITY_OPTIONS.filter(
        (option) => String(option.value) !== String(TASK_PRIORITY_NOT_SET)
      )
    : TASK_PRIORITY_OPTIONS;

// RHF validate for the priority toggle group. Passed via the component's
// `rules` prop, which REPLACES its built-in required check — so emptiness is
// re-handled here (deselecting the active toggle yields null). "Not Set" is
// only rejected when the practice requires a real priority; an old task
// stored with 0 then blocks save until a real priority is picked.
export const validateTaskPriority =
  (isPriorityRequired: boolean) =>
  (value: unknown): true | string => {
    if (value == null || String(value) === "") {
      return "Select a priority level";
    }
    if (isPriorityRequired && String(value) === String(TASK_PRIORITY_NOT_SET)) {
      return "Select a priority level";
    }
    return true;
  };

const HTML_TAG_RE = /<\/?[a-z][\s\S]*>/i;

// Rich task descriptions (SN-17576) persist as HTML. Legacy/plain descriptions
// keep the old plain-text round-trip so existing tasks are unaffected.
const toEditorDescription = (
  raw: string | null | undefined,
  preserveHtml: boolean,
  preserveMentions = false
) => {
  const value = raw ?? "";
  if (
    (preserveHtml || (preserveMentions && hasMentionPills(value))) &&
    HTML_TAG_RE.test(value)
  ) {
    return value;
  }
  return getHtmlLikeText(value);
};

/**
 * Whether a description is saved as HTML. Rich text (SN-17576) always is. With @mentions on
 * (TEAM_MENTIONS_FEATURE), a plain-text (textOnly) description that holds mention pills is too:
 * the backend finds who to notify by parsing the pills, and converting to text would drop them.
 */
export const shouldPreserveDescriptionHtml = (
  description: string | null | undefined,
  enableTaskRichText: boolean,
  enableMentions: boolean
) => enableTaskRichText || (enableMentions && hasMentionPills(description));

export const formatStaffTaskToInput = (
  data: StaffTaskV2FormValues,
  preserveHtml = false,
  includeColor = false,
  // TEAMS_FEATURE: `team` is a write key an older backend 400s, so it is sent only with the flag on.
  includeTeam = false
) => {
  const result: StaffTaskInput = {
    id: data.id,
    title: data.title,
    description: preserveHtml
      ? data.description
      : convertHtmlToText(data.description),
    priority: Number(data.priority) || 0,
    status: data.status ? Number(data.status) : undefined,
    assignee: data.assignee?.value ? Number(data.assignee?.value) : null,
    assignees: data.assignees
      .filter((assignee) => assignee.value !== data.assignee?.value)
      .map((assignee) => ({
        user: Number(assignee.value)
      })),
    due_at: data.due_at
      ? format(new Date(data.due_at), "yyyy-MM-dd'T'HH:mm:ss")
      : null,
    patient: data.patient?.id ? Number(data.patient?.id) : null
  };

  if (includeColor) {
    result.color = data.color ?? null;
  }

  if (includeTeam) {
    result.team = data.team?.value ? Number(data.team.value) : null;
  }

  return result;
};

export const getInitialValues = (
  editMode: StaffTask | undefined,
  currentUser: CurrentUser,
  preserveHtml = false,
  preserveMentions = false
): StaffTaskV2FormValues => {
  if (editMode) {
    return {
      id: editMode?.id,
      priority: String(editMode?.priority) || "",
      title: editMode?.title ?? "",
      status: editMode.status,
      description: toEditorDescription(
        editMode?.description,
        preserveHtml,
        preserveMentions
      ),
      due_at: editMode?.due_at
        ? formatBackendDateOnly(editMode?.due_at, "MM/dd/yyyy")
        : "",
      assignee: editMode?.assignee
        ? {
            label: String(editMode?.assignee?.fullName),
            value: String(editMode?.assignee.id)
          }
        : null,
      assignees: editMode?.assignees
        ? editMode?.assignees.map((assignee) => ({
            label: String(assignee.user?.fullName),
            value: String(assignee.user?.id)
          }))
        : [],
      team: editMode?.team
        ? {
            label: editMode.team.name,
            value: String(editMode.team.id),
            color: editMode.team.color
          }
        : null,
      patient: editMode?.patient,
      color: editMode?.color ?? null
    };
  }

  return {
    title: "",
    priority: "0",
    description: "",
    due_at: null,
    assignee: { label: currentUser.fullName, value: String(currentUser.id) },
    assignees: [],
    team: null,
    patient: null,
    color: null
  };
};

// Rendered task descriptions can contain user-authored links (SN-17576). Force them
// to open in a new tab (so a click never blows away the SPA) and neutralize dangerous
// href schemes (javascript:/data: are an XSS vector). Reputation/virus scanning of the
// target URL is out of scope — a separate task.
export const prepareTaskDescriptionHtml = (html: string): string => {
  if (typeof document === "undefined" || !html) {
    return html;
  }
  const container = document.createElement("div");
  container.innerHTML = html;
  container.querySelectorAll("a").forEach((anchor) => {
    const href = anchor.getAttribute("href") ?? "";
    if (/^\s*(javascript|data):/i.test(href)) {
      anchor.removeAttribute("href");
    }
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener noreferrer");
  });
  return container.innerHTML;
};

export const getCardPosition = (index: number, count: number) => {
  if (count === 1) {
    return "single";
  } else if (index === 0) {
    return "first";
  } else if (index === count - 1) {
    return "last";
  }

  return undefined;
};
